import {
  BankingApplicationDocument,
  binaryValueFormatter,
  DO_APPLICATION_TYPE,
  getPrefillBannerSectionStatus,
  useSectionFormHelpers,
} from 'client-experience-toolbox';
import { FormControl, FormControlLabel, FormHelperText, Radio, RadioGroup } from 'drift-ui';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useFormNetworkCalls } from 'utils/useFormNetworkCalls';
import { useUpdateApplication } from 'utils/useUpdateApplication';
import { sendDOFirebaseEvent } from 'utils/utils';
import baseStyles from './BaseFormStyles.scss';
import styles from './BusinessOwnership.scss';
import cx from 'classnames';
import { ContinueButton } from 'components/ContinueButton';
import { useApplication } from 'hooks/useApplication';
import { PrefillBanner } from 'components/PrefillBanner';

export const BusinessOwnership = () => {
  const { application } = useApplication(DO_APPLICATION_TYPE.BANKING);
  const applicationData = application?.applicationData as Partial<BankingApplicationDocument>['applicationData'];
  const applicants = applicationData?.applicants;
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const updateApplication = useUpdateApplication(DO_APPLICATION_TYPE.BANKING);
  const [isPatching, setIsPatching] = useState(false);
  const dispatch = useDispatch();
  const { patchApplication, patchFullApplication, patchApplicationPullData } = useFormNetworkCalls(
    DO_APPLICATION_TYPE.BANKING,
  );
  const { clearValidation, presenceValidatorGenerator, handleInputFocus, binaryHandlerGenerator } =
    useSectionFormHelpers({
      errorSetter: setErrors,
      patchApplication,
      application,
      updateApplication,
      fireBaseLogEvent: sendDOFirebaseEvent,
    });

  const { t } = useTranslation();
  const prefillBannerStatus = getPrefillBannerSectionStatus(
    (application as BankingApplicationDocument)?.prefilledFields,
    'businessOwnership',
  );

  const removeAdditionalApplicants = async () => {
    if (!application?._id || !applicants) return;

    const additionalApplicants = applicants.filter(
      (applicant) => applicant.applicantId !== application.primaryApplicantId,
    );

    for (const applicant of additionalApplicants) {
      if (applicant.key) {
        dispatch({ type: 'REMOVE_ADDITIONAL_OWNER', owner: applicant, applicationType: DO_APPLICATION_TYPE.BANKING });
        await patchApplicationPullData(application._id, {
          'applicationData.applicants': { key: applicant.key },
        });
      }
    }
  };

  const fieldValidators: { [key: string]: () => boolean } = {
    hasMajorOwner: presenceValidatorGenerator({
      key: 'applicationData.company.hasMajorOwner',
      errorText: t('Required'),
    }),
  };

  const handleContinueClick = async () => {
    const results = Object.values(fieldValidators).map((validator, index) => ({
      index,
      isValid: validator(),
    }));
    const firstInvalid = results.find(({ isValid }) => !isValid);

    if (firstInvalid) {
      return;
    }

    if (applicationData?.company?.hasMajorOwner === false) {
      setIsPatching(true);
      await removeAdditionalApplicants();
      const filteredApplicants = applicants?.filter(
        (applicant) => applicant.applicantId === application.primaryApplicantId,
      );
      filteredApplicants[0].ownershipPercentage = null;
      await patchFullApplication(
        setIsPatching,
        {
          ...applicationData,
          controlProng:
            applicationData.controlProng?.key !== 'controlProng'
              ? { key: null, jobTitle: null }
              : applicationData.controlProng,
          applicants: filteredApplicants,
        },
        {
          businessOwnership: {
            completedAt: new Date().valueOf(),
          },
          controlProng: {
            completedAt: null,
          },
        },
      );
      return;
    }

    await patchFullApplication(setIsPatching);
  };

  return (
    <div className={cx(baseStyles.formContainer, styles.questionContainer)}>
      <div className={styles.questionFormContainer}>
        <div>
          <p
            className={styles.questionTitle}
            style={{ color: errors['applicationData.company.hasMajorOwner'] ? '#EF3824' : '#444' }}
          >
            {t('digital_onboarding.banking.business_ownership.ownership_question')}
          </p>
          {prefillBannerStatus !== 'none' && (
            <div className={styles.bannerContainer}>
              <PrefillBanner hasLockedFields={false} />
            </div>
          )}
          <FormControl error={!!errors['applicationData.company.hasMajorOwner']}>
            <RadioGroup
              data-nid-target="business-ownership-has-major-owner"
              value={binaryValueFormatter(applicationData?.company?.hasMajorOwner)}
              onChange={binaryHandlerGenerator('applicationData.company.hasMajorOwner', clearValidation)}
              onFocus={handleInputFocus}
            >
              <FormControlLabel
                value="yes"
                control={
                  <Radio
                    slotProps={{
                      input: {
                        'data-nid-target': 'business-ownership-has-major-owner-yes',
                      } as React.InputHTMLAttributes<HTMLInputElement>,
                    }}
                  />
                }
                label={t('yes')}
              />
              <FormControlLabel
                value="no"
                control={
                  <Radio
                    slotProps={{
                      input: {
                        'data-nid-target': 'business-ownership-has-major-owner-no',
                      } as React.InputHTMLAttributes<HTMLInputElement>,
                    }}
                  />
                }
                label={t('no')}
              />
            </RadioGroup>
            {errors['applicationData.company.hasMajorOwner'] && (
              <FormHelperText>{errors['applicationData.company.hasMajorOwner']}</FormHelperText>
            )}
          </FormControl>
        </div>
      </div>
      <div className={styles.footer}>
        <ContinueButton
          buttonStyles={baseStyles.buttonStyle}
          handleContinueClick={handleContinueClick}
          isPatching={isPatching}
          title={t('digital_onboarding.next')}
        />
      </div>
    </div>
  );
};
